import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { format } from 'date-fns'
import { Calendar } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'

const DEFAULT_MIN_YEAR = 2015
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const
const POPOVER_WIDTH = 264
const POPOVER_ESTIMATED_HEIGHT = 280
const VIEWPORT_INSET = 12
const TRIGGER_GAP = 8
const POPOVER_Z = 9999

export interface MonthYearPickerProps {
  value: string
  onChange: (value: string) => void
  minYear?: number
  maxYear?: number
  disabled?: boolean
  readOnly?: boolean
  className?: string
  placeholder?: string
  dataTestId?: string
  calendarButtonDataTestId?: string
  clearButtonDataTestId?: string
  thisMonthButtonDataTestId?: string
  yearSelectDataTestId?: string
  /** When true, all 12 months stay selectable even for the current year. */
  allowAllMonths?: boolean
}

function parseMonthYearValue(value: string): { year: number; month: number } | null {
  const trimmed = String(value ?? '').trim()
  const match = trimmed.match(/^(\d{4})-(\d{2})$/)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null
  return { year, month }
}

function toMonthYearValue(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

function formatMonthYearDisplay(value: string): string {
  const parsed = parseMonthYearValue(value)
  if (!parsed) return ''
  return format(new Date(parsed.year, parsed.month - 1, 1), 'MMMM yyyy')
}

function buildMonthYearYearOptions(minYear: number, maxYear: number): number[] {
  if (maxYear < minYear) return []
  return Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i)
}

function clampYear(year: number, minYear: number, maxYear: number): number {
  return Math.min(maxYear, Math.max(minYear, year))
}

function getNowParts(): { year: number; month: number } {
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
}

function isMonthDisabled(
  year: number,
  month: number,
  minYear: number,
  maxYear: number,
  allowAllMonths = false,
): boolean {
  if (year < minYear || year > maxYear) return true
  if (allowAllMonths) return false
  const now = getNowParts()
  if (year === now.year && now.year === maxYear && month > now.month) return true
  return false
}

function clampToAllowedMonthYear(minYear: number, maxYear: number): { year: number; month: number } {
  const now = getNowParts()
  const year = clampYear(now.year, minYear, maxYear)
  if (year < now.year) return { year, month: 12 }
  if (year > now.year) return { year, month: 1 }
  return { year, month: now.month }
}

export function MonthYearPicker({
  value,
  onChange,
  minYear = DEFAULT_MIN_YEAR,
  maxYear,
  disabled = false,
  readOnly = false,
  className,
  placeholder = 'Select month',
  dataTestId,
  calendarButtonDataTestId,
  clearButtonDataTestId,
  thisMonthButtonDataTestId,
  yearSelectDataTestId,
  allowAllMonths = false,
}: MonthYearPickerProps) {
  const resolvedMaxYear = maxYear ?? new Date().getFullYear()
  const nonInteractive = disabled || readOnly
  const parsed = parseMonthYearValue(value)
  const displayValue = formatMonthYearDisplay(value)

  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(() =>
    clampYear(parsed?.year ?? getNowParts().year, minYear, resolvedMaxYear),
  )
  const [menuPosition, setMenuPosition] = useState<{
    top: number | 'auto'
    bottom: number | 'auto'
    left: number
  }>({ top: 0, bottom: 'auto', left: 0 })

  const fieldRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const yearOptions = useMemo(
    () => buildMonthYearYearOptions(minYear, resolvedMaxYear),
    [minYear, resolvedMaxYear],
  )

  const resolvedCalendarButtonTestId =
    calendarButtonDataTestId ?? (dataTestId ? `${dataTestId}-calendar` : undefined)

  const close = useCallback(() => setOpen(false), [])

  const openPicker = useCallback(() => {
    if (nonInteractive) return
    setViewYear(clampYear(parsed?.year ?? getNowParts().year, minYear, resolvedMaxYear))
    setOpen(true)
  }, [nonInteractive, parsed?.year, minYear, resolvedMaxYear])

  const togglePicker = useCallback(() => {
    if (nonInteractive) return
    if (open) {
      close()
      return
    }
    openPicker()
  }, [nonInteractive, open, close, openPicker])

  const updateMenuPosition = useCallback(() => {
    const trigger = fieldRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    let left = rect.left
    if (left + POPOVER_WIDTH > viewportWidth - VIEWPORT_INSET) {
      left = Math.max(VIEWPORT_INSET, viewportWidth - POPOVER_WIDTH - VIEWPORT_INSET)
    }
    left = Math.max(VIEWPORT_INSET, left)

    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top
    const openAbove =
      spaceBelow < POPOVER_ESTIMATED_HEIGHT && spaceAbove > spaceBelow

    setMenuPosition(
      openAbove
        ? { top: 'auto', bottom: viewportHeight - rect.top + TRIGGER_GAP, left }
        : { top: rect.bottom + TRIGGER_GAP, bottom: 'auto', left },
    )
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    updateMenuPosition()
    const onScrollOrResize = () => updateMenuPosition()
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [open, updateMenuPosition])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (fieldRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  const commitValue = (next: string) => {
    onChange(next)
    close()
  }

  const handleMonthSelect = (month: number) => {
    if (isMonthDisabled(viewYear, month, minYear, resolvedMaxYear, allowAllMonths)) return
    commitValue(toMonthYearValue(viewYear, month))
  }

  const handleClear = () => commitValue('')

  const handleThisMonth = () => {
    const next = clampToAllowedMonthYear(minYear, resolvedMaxYear)
    commitValue(toMonthYearValue(next.year, next.month))
  }

  return (
    <div className="relative" ref={fieldRef}>
      <input
        type="text"
        readOnly
        disabled={disabled}
        value={displayValue}
        placeholder={placeholder}
        data-testid={dataTestId}
        onClick={togglePicker}
        onKeyDown={(event) => {
          if (nonInteractive) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            togglePicker()
          }
        }}
        className={cn(
          'w-full',
          disabled && 'cursor-not-allowed bg-slate-100 text-slate-500',
          readOnly && !disabled && 'cursor-default',
          !nonInteractive && 'cursor-pointer',
          className,
          'pr-10',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={nonInteractive}
        data-testid={resolvedCalendarButtonTestId}
        aria-label="Open month picker"
        className={cn(
          'absolute right-3 top-1/2 z-10 -translate-y-1/2 border-0 bg-transparent p-0',
          nonInteractive
            ? readOnly && !disabled
              ? 'cursor-default opacity-100'
              : 'cursor-not-allowed opacity-50'
            : 'cursor-pointer',
        )}
        onMouseDown={(event) => {
          event.preventDefault()
        }}
        onClick={togglePicker}
      >
        <Calendar className="h-5 w-5 text-gray-400" />
      </button>

      {open && !nonInteractive && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-label="Select month and year"
              className="flex w-[264px] flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 shadow-lg"
              style={{
                position: 'fixed',
                top: menuPosition.top,
                bottom: menuPosition.bottom,
                left: menuPosition.left,
                zIndex: POPOVER_Z,
              }}
            >
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-500">Year</span>
                <select
                  value={viewYear}
                  onChange={(event) => setViewYear(Number(event.target.value))}
                  data-testid={yearSelectDataTestId}
                  className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>

              <div className="grid grid-cols-3 gap-1.5" role="listbox" aria-label="Months">
                {SHORT_MONTHS.map((label, index) => {
                  const month = index + 1
                  const selected = parsed?.year === viewYear && parsed.month === month
                  const monthDisabled = isMonthDisabled(viewYear, month, minYear, resolvedMaxYear, allowAllMonths)
                  return (
                    <button
                      key={label}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      aria-disabled={monthDisabled}
                      disabled={monthDisabled}
                      onClick={() => handleMonthSelect(month)}
                      className={cn(
                        'h-8 rounded-md text-sm font-medium',
                        selected && 'bg-blue-500 text-white hover:bg-blue-600',
                        !selected && !monthDisabled && 'text-slate-800 hover:bg-slate-100 focus-visible:bg-slate-100',
                        monthDisabled && 'cursor-not-allowed text-slate-300',
                      )}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>

              <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
                <button
                  type="button"
                  data-testid={clearButtonDataTestId}
                  onClick={handleClear}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  Clear
                </button>
                <button
                  type="button"
                  data-testid={thisMonthButtonDataTestId}
                  onClick={handleThisMonth}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  This month
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
